import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { Search, Filter, Eye, CheckCircle, Clock, XCircle, Trash2, Mail, Phone, Building, Edit, X } from 'lucide-react';
import * as contactService from '../services/contactService';

interface Contact {
  id?: string;
  _id?: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  products?: string[];
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  createdAt?: string;
}

const dummyContacts: Contact[] = [
  {
    id: '1',
    name: 'Mehmet Özkan',
    email: 'mehmet.ozkan@ozkaninşaat.com',
    phone: '+90 212 555 12 34',
    company: 'Özkan İnşaat Makina San. ve Tic. Ltd. Şti.',
    message: 'Yeni projemiz için ekipman ihtiyacımız var. Ekskavatör, buldozer ve yükleyici için fiyat teklifi istiyorum.',
    products: ['caterpillar-320d-ekskavatoru', 'komatsu-d65-buldozer'],
    status: 'new',
    createdAt: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    name: 'Ali Demir',
    email: 'ali.demir@gmail.com',
    phone: '+90 533 987 65 43',
    message: 'Kendi arsam için küçük bir ekskavatör kiralamak istiyorum. 1 haftalık kiralama için fiyat nedir?',
    status: 'in_progress',
    createdAt: '2024-01-14T14:20:00Z'
  }
];

const RFQ: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Contact'ları API'den yükle
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const contactsData = await contactService.getContacts();
      console.log('Contacts received:', contactsData);
      setContacts(contactsData);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Talepler yüklenirken hata oluştu');
      setContacts(dummyContacts); // Fallback to dummy data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleStatusChange = async (contact: Contact, newStatus: string) => {
    try {
      const contactId = contact.id || contact._id;
      if (!contactId) {
        toast.error('Contact ID bulunamadı');
        return;
      }
      await contactService.updateContactStatus(contactId, newStatus);
      toast.success('Durum başarıyla güncellendi');
      fetchContacts();
    } catch (error) {
      console.error('Error updating contact status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const handleDelete = async (contact: Contact) => {
    if (window.confirm('Bu talebi silmek istediğinizden emin misiniz?')) {
      try {
        const contactId = contact.id || contact._id;
        if (!contactId) {
          toast.error('Contact ID bulunamadı');
          return;
        }
        await contactService.deleteContact(contactId);
        toast.success('Talep başarıyla silindi');
        fetchContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Talep silinirken hata oluştu');
      }
    }
  };

  const handleViewDetails = (contact: Contact) => {
    setSelectedContact(contact);
    setShowDetails(true);
  };

  const handleNewContact = () => {
    setEditingContact(null);
    setShowForm(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setShowForm(true);
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      const contactData = {
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        phone: formData.get('phone') as string,
        company: formData.get('company') as string,
        message: formData.get('message') as string,
        products: (formData.get('products') as string || '').split(',').map(p => p.trim()).filter(p => p),
        status: formData.get('status') as string || 'new'
      };

      if (editingContact) {
        // Update contact (API'de update endpoint'i yok, sadece status update var)
        toast.success('Contact güncelleme API endpoint\'i henüz mevcut değil');
      } else {
        await contactService.createContact(contactData);
        toast.success('Contact başarıyla oluşturuldu');
      }
      
      setShowForm(false);
      setEditingContact(null);
      fetchContacts();
    } catch (error: any) {
      console.error('Error saving contact:', error);
      toast.error('Contact kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'new':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'in_progress':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'resolved':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'closed':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <Clock className="h-4 w-4 mr-1" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 mr-1" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4 mr-1" />;
      case 'closed':
        return <XCircle className="h-4 w-4 mr-1" />;
      default:
        return <Clock className="h-4 w-4 mr-1" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'Yeni';
      case 'in_progress':
        return 'İşleniyor';
      case 'resolved':
        return 'Çözüldü';
      case 'closed':
        return 'Kapatıldı';
      default:
        return status;
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (contact.company && contact.company.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = !statusFilter || contact.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (showDetails && selectedContact) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Talep Detayı</h1>
              <p className="text-gray-600 mt-2">#{selectedContact.id || selectedContact._id}</p>
            </div>
            <button
              onClick={() => setShowDetails(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Listeye Dön
            </button>
          </div>

          {/* Contact Details */}
          <div className="bg-white rounded-lg shadow-card p-6 space-y-6">
            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">İletişim Bilgileri</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-20">Ad:</span>
                    <span className="text-gray-900">{selectedContact.name}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    <a href={`mailto:${selectedContact.email}`} className="text-primary-600 hover:text-primary-800">
                      {selectedContact.email}
                    </a>
                  </div>
                  {selectedContact.phone && (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <a href={`tel:${selectedContact.phone}`} className="text-primary-600 hover:text-primary-800">
                        {selectedContact.phone}
                      </a>
                    </div>
                  )}
                  {selectedContact.company && (
                    <div className="flex items-center">
                      <Building className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="text-gray-900">{selectedContact.company}</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Talep Bilgileri</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-20">Durum:</span>
                    <span className={getStatusBadge(selectedContact.status)}>
                      {getStatusIcon(selectedContact.status)}
                      {getStatusText(selectedContact.status)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-20">Tarih:</span>
                    <span className="text-gray-900">
                      {selectedContact.createdAt ? new Date(selectedContact.createdAt).toLocaleDateString('tr-TR') : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Mesaj</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-900 whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
            </div>

            {/* Products */}
            {selectedContact.products && selectedContact.products.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">İlgili Ürünler</h3>
                <div className="grid grid-cols-1 gap-2">
                  {selectedContact.products.map((product, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <span className="text-gray-900">{product}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Durum Değiştir</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleStatusChange(selectedContact, 'new')}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                  disabled={selectedContact.status === 'new'}
                >
                  Yeni
                </button>
                <button
                  onClick={() => handleStatusChange(selectedContact, 'in_progress')}
                  className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
                  disabled={selectedContact.status === 'in_progress'}
                >
                  İşleme Al
                </button>
                <button
                  onClick={() => handleStatusChange(selectedContact, 'resolved')}
                  className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                  disabled={selectedContact.status === 'resolved'}
                >
                  Çözüldü
                </button>
                <button
                  onClick={() => handleStatusChange(selectedContact, 'closed')}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  disabled={selectedContact.status === 'closed'}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">RFQ Talepleri</h1>
              <p className="text-gray-600 mt-2">Müşteri talepleri ve fiyat tekliflerini yönetin</p>
            </div>
            <button
              onClick={handleNewContact}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2 mt-4 sm:mt-0"
            >
              <Mail className="h-4 w-4" />
              <span>Yeni Contact</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-card p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Ad, email veya şirket ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
              >
                <option value="">Tüm Durumlar</option>
                <option value="new">Yeni</option>
                <option value="in_progress">İşleniyor</option>
                <option value="resolved">Çözüldü</option>
                <option value="closed">Kapatıldı</option>
              </select>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-end">
              <div className="text-sm text-gray-600">
                Toplam: <span className="font-semibold">{filteredContacts.length}</span> talep
              </div>
            </div>
          </div>
        </div>

        {/* Contacts List */}
        <div className="bg-white rounded-lg shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Talepler ({filteredContacts.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="text-gray-500">Talepler yükleniyor...</div>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İletişim
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Şirket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredContacts.map((contact) => (
                    <tr key={contact.id || contact._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                          <div className="text-sm text-gray-500">{contact.email}</div>
                          {contact.phone && (
                            <div className="text-sm text-gray-500">{contact.phone}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{contact.company || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(contact.status)}>
                          {getStatusIcon(contact.status)}
                          {getStatusText(contact.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString('tr-TR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(contact)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Detayları Gör"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditContact(contact)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Düzenle"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(contact)}
                            className="text-red-600 hover:text-red-900"
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Contact Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingContact ? 'Contact Düzenle' : 'Yeni Contact'}
                  </h3>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSubmit(formData);
                }}
                className="p-6 space-y-6"
              >
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingContact?.name || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ad Soyad girin"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-posta *
                  </label>
                  <input
                    type="email"
                    name="email"
                    defaultValue={editingContact?.email || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="E-posta adresini girin"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      defaultValue={editingContact?.phone || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="+90 xxx xxx xx xx"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Şirket
                    </label>
                    <input
                      type="text"
                      name="company"
                      defaultValue={editingContact?.company || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Şirket adını girin"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mesaj *
                  </label>
                  <textarea
                    rows={4}
                    name="message"
                    defaultValue={editingContact?.message || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Mesajınızı girin"
                    required
                  />
                </div>

                {/* Products */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İlgili Ürünler
                  </label>
                  <input
                    type="text"
                    name="products"
                    defaultValue={editingContact?.products?.join(', ') || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ürün slug'larını virgülle ayırarak girin"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Örn: caterpillar-320d-ekskavatoru, komatsu-d65-buldozer
                  </p>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    name="status"
                    defaultValue={editingContact?.status || 'new'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="new">Yeni</option>
                    <option value="in_progress">İşlemde</option>
                    <option value="resolved">Çözüldü</option>
                    <option value="closed">Kapatıldı</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingContact ? 'Güncelle' : 'Kaydet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RFQ;

